import React, { useState, useEffect } from 'react'
import { Button, Typography } from '~/components/_atoms'
import CoachInfoDialog from './CoachInfoDialog'
import CalendarButton from './CalendarButton'
import axios from 'axios'
import RoundtableRegistrationModal from './RoundtableRegistrationModal'
import useTranslation from '../../shared/TranslationProvider'
import { useFormatTime } from '~/hooks/useFormatTime'

const WebinarListItem = React.forwardRef(
  (
    {
      webinar,
      currentUser,
      selected = false,
      isPartnerCandidate,
      showDisclaimer,
      ...props
    },
    ref,
  ) => {
    const { t, locale } = useTranslation()
    const [showCoachInfo, setShowCoachInfo] = useState(false)
    const [isRegistered, setIsRegistered] = useState(webinar.is_registered)
    const isGecEvent = webinar.is_gec_event
    const [isLoading, setIsLoading] = useState(
      webinar.is_registered && !isPartnerCandidate && !isGecEvent,
    )
    const [joinUrl, setJoinUrl] = useState(null)
    const isPast = webinar.is_past
    const hasLink = webinar.registration_link && !isPast
    const coachName = webinar.coach?.first_name
      ? `Coach ${webinar.coach.first_name}`
      : 'us'
    const noSeatsRemaining = webinar.seats_remaining === 0
    const isRoundtable = webinar.is_roundtable
    const isCancelled = webinar.is_cancelled
    const formatTime = useFormatTime(locale)

    const getSeatStatusMessage = (seatsRemaining) => {
      if (seatsRemaining === 0) return ' - Event Full'
      if (seatsRemaining === 1) return ' - 1 Seat Remaining'
      if (seatsRemaining > 1) return ` - ${seatsRemaining} Seats Remaining`
      return ''
    }

    const gecEventImageUrl =
      'https://www-yns-com.s3.us-east-2.amazonaws.com/networking/gec_events/gec_event_image.png'

    const dateTimeDuration = webinar.duration
      ? `${formatTime(webinar.start_at, 'short')} - ${formatTime(webinar.start_at, 'time')} - ${formatTime(new Date(new Date(webinar.start_at).getTime() + webinar.duration * 60000), 'time')}`
      : formatTime(webinar.start_at)

    useEffect(() => {
      if (!webinar.is_registered || isPartnerCandidate || isGecEvent) {
        setIsLoading(false)
        return
      }

      const checkRegistration = async () => {
        try {
          const response = await axios.get(
            `/coach_services/check_url_and_registration?event_id=${webinar.id}`,
          )
          setIsRegistered(response.data.is_registered)
          setJoinUrl(response.data.join_url)
        } catch (error) {
          console.error('Error checking registration status:', error)
        } finally {
          setIsLoading(false)
        }
      }

      checkRegistration()
    }, [webinar.id, webinar.is_registered, isPartnerCandidate, isGecEvent])

    const handleCoachClick = () => {
      if (webinar.coach?.id) {
        setShowCoachInfo(true)
      }
    }

    return (
      <div
        ref={ref}
        className={`webinar-event-item !rounded-xl border border-willow-brook-100 bg-willow-brook-100 p-4 @container ${
          selected ? 'outline-3 outline outline-willow-brook-700' : ''
        }`}
        {...props}
      >
        <div className="flex flex-col gap-4 @xl:flex-row @7xl:flex-row">
          {isGecEvent ? (
            <div className="flex flex-col items-center justify-center @xl:min-w-[200px] @7xl:min-w-[200px]">
              <img
                src={gecEventImageUrl}
                alt="GEC Event"
                className="h-32 w-32 object-contain"
              />
            </div>
          ) : (
            !isPartnerCandidate && (
              // Coach Info - Left Side
              <div className="flex cursor-pointer flex-col items-center justify-center @xl:min-w-[200px] @7xl:min-w-[200px]">
                {webinar.coach?.transparent_avatar_url ? (
                  <div className="h-30 w-30 relative inline-flex items-center justify-center rounded-full bg-willow-brook-200 p-6">
                    <img
                      src={webinar.coach.transparent_avatar_url}
                      alt={coachName}
                      className="h-24 w-24 cursor-pointer object-contain"
                      onClick={handleCoachClick}
                    />
                  </div>
                ) : webinar.coach?.avatar_url ? (
                  <img
                    src={webinar.coach.avatar_url}
                    alt={coachName}
                    className="h-16 w-16 cursor-pointer rounded-full object-cover"
                    onClick={handleCoachClick}
                  />
                ) : null}
              </div>
            )
          )}

          {/* Content and Buttons Container */}
          <div className="flex flex-col gap-4 @xl:flex-1 @4xl:flex-1">
            {/* Event Details - Middle */}
            <div>
              <Typography variant="title4" color="secondary">
                {showDisclaimer
                  ? `${webinar.webinar_type} ${t('english_disclaimer')}`
                  : webinar.webinar_type}
              </Typography>
              <Typography variant="subtitle">
                {dateTimeDuration}
                {!isCancelled && getSeatStatusMessage(webinar.seats_remaining)}
              </Typography>
              <Typography variant="small">{webinar.description}</Typography>
            </div>

            {/* Action Buttons - Bottom Row */}
            <div className="@4xl:tw-hidden flex flex-col gap-2 @xl:flex-row @xl:items-center @xl:justify-start">
              {isLoading ? (
                <Button color="secondary" size="sm" disabled>
                  {t('loading')}
                </Button>
              ) : noSeatsRemaining ? (
                <Button
                  variant="outline"
                  color="secondary"
                  size="sm"
                  className="coming-soon"
                  disabled
                >
                  {t('event_full')}
                </Button>
              ) : isCancelled && isRoundtable ? (
                <Button
                  variant="outline"
                  color="secondary"
                  size="sm"
                  className="coming-soon"
                  disabled
                >
                  {t('event_cancelled')}
                </Button>
              ) : isRegistered ? null : hasLink && isRoundtable ? (
                <RoundtableRegistrationModal
                  variant="solid"
                  color="secondary"
                  size="sm"
                  link={webinar.registration_link}
                  name={webinar.webinar_type}
                />
              ) : hasLink && !isRoundtable ? (
                <Button
                  variant="solid"
                  color="secondary"
                  size="sm"
                  href={webinar.registration_link}
                  target={isPartnerCandidate || isGecEvent ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                >
                  {t('register_now')}
                </Button>
              ) : !isPast ? (
                <Button
                  variant="outline"
                  color="secondary"
                  size="sm"
                  className="coming-soon"
                  disabled
                >
                  {t('registration_link_coming_soon')}
                </Button>
              ) : null}
              {isRegistered && !isPast && (
                <Button
                  variant="solid"
                  color="secondary"
                  size="sm"
                  href={joinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('join_webinar')}
                </Button>
              )}

              {!noSeatsRemaining &&
                !isCancelled &&
                !isPast &&
                !isPartnerCandidate &&
                !isGecEvent && (
                  <CalendarButton
                    event={webinar}
                    joinUrl={joinUrl}
                    isRegistered={isRegistered}
                  />
                )}
            </div>
          </div>

          {/* Action Buttons - Right Side - only visible at 7xl */}
          <div className="tw-hidden flex-col items-end justify-center gap-2 @4xl:flex">
            {isLoading ? (
              <Button
                color="secondary"
                size="sm"
                disabled
                className="@4xl:w-full"
              >
                {t('loading')}
              </Button>
            ) : noSeatsRemaining ? (
              <Button
                variant="outline"
                color="secondary"
                size="sm"
                className="coming-soon"
                disabled
              >
                {t('event_full')}
              </Button>
            ) : isCancelled && isRoundtable ? (
              <Button
                variant="outline"
                color="secondary"
                size="sm"
                className="coming-soon"
                disabled
              >
                {t('event_cancelled')}
              </Button>
            ) : isRegistered ? null : hasLink && isRoundtable ? (
              <RoundtableRegistrationModal
                variant="solid"
                color="secondary"
                size="sm"
                link={webinar.registration_link}
                className="@4xl:w-full"
                name={webinar.webinar_type}
              />
            ) : hasLink && !isRoundtable ? (
              <Button
                variant="solid"
                color="secondary"
                size="sm"
                href={webinar.registration_link}
                target={isPartnerCandidate || isGecEvent ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="@4xl:w-full"
              >
                {t('register_now')}
              </Button>
            ) : !isPast ? (
              <Button
                variant="outline"
                color="secondary"
                size="sm"
                className="coming-soon @4xl:w-full"
                disabled
              >
                {t('registration_link_coming_soon')}
              </Button>
            ) : null}

            {isRegistered && !isPast && (
              <Button
                variant="solid"
                color="secondary"
                size="sm"
                className="@4xl:w-full"
                href={joinUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('join_webinar')}
              </Button>
            )}

            {!noSeatsRemaining &&
              !isCancelled &&
              !isPast &&
              !isPartnerCandidate &&
              !isGecEvent && (
                <CalendarButton
                  event={webinar}
                  joinUrl={joinUrl}
                  isRegistered={isRegistered}
                />
              )}
          </div>
        </div>
        {webinar.coach?.id && (
          <CoachInfoDialog
            coachId={webinar.coach.id}
            open={showCoachInfo}
            onClose={() => setShowCoachInfo(false)}
          />
        )}
      </div>
    )
  },
)

WebinarListItem.displayName = 'WebinarListItem'

export default WebinarListItem
